import { useTranslation } from "react-i18next";
import { Block } from "./shared/Block";
import { useAddImageObject } from "@/components/canvas/hooks/event/useAddImageEvent";
import { useState, useMemo } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";

// Define the structure for our shape library
interface ShapeCategory {
  name: string;
  description: string;
  items: ShapeItem[];
}

interface ShapeItem {
  name: string;
  source: string;
  tags: string[];
}

// Example of organizing shapes by category
const shapeLibrary: ShapeCategory[] = [
  {
    name: "Food",
    description: "Food and beverage shapes for culinary designs",
    items: [
      {
        name: "Burger",
        source: "/shapes/food/burger-1-svgrepo-com.svg",
        tags: ["food", "fast food", "burger"],
      },
      {
        name: "Cake",
        source: "/shapes/food/cake-2-svgrepo-com.svg",
        tags: ["food", "dessert", "cake", "sweet"],
      },
      {
        name: "Candy",
        source: "/shapes/food/candy-1-svgrepo-com.svg",
        tags: ["food", "sweet", "candy", "dessert"],
      },
      {
        name: "Carrot",
        source: "/shapes/food/carrot-1-svgrepo-com.svg",
        tags: ["food", "vegetable", "carrot", "healthy"],
      },
      {
        name: "Cauliflower",
        source: "/shapes/food/cauliflower-1-svgrepo-com.svg",
        tags: ["food", "vegetable", "cauliflower", "healthy"],
      },
      {
        name: "Cheese",
        source: "/shapes/food/cheese-1-svgrepo-com.svg",
        tags: ["food", "dairy", "cheese"],
      },
      {
        name: "Chicken Leg",
        source: "/shapes/food/chicken-leg-1-svgrepo-com.svg",
        tags: ["food", "meat", "chicken"],
      },
      {
        name: "Coffee",
        source: "/shapes/food/coffee-1-svgrepo-com.svg",
        tags: ["food", "beverage", "coffee", "drink"],
      },
      {
        name: "Drink",
        source: "/shapes/food/drink-1-svgrepo-com.svg",
        tags: ["food", "beverage", "drink"],
      },
      {
        name: "Egg",
        source: "/shapes/food/egg-1-svgrepo-com.svg",
        tags: ["food", "egg", "breakfast"],
      },
      {
        name: "Face",
        source: "/shapes/food/face-1-svgrepo-com.svg",
        tags: ["food", "face", "emoji"],
      },
      {
        name: "Ice Cream",
        source: "/shapes/food/ice-cream-1-svgrepo-com.svg",
        tags: ["food", "dessert", "ice cream", "sweet"],
      },
      {
        name: "Lemon",
        source: "/shapes/food/lemon-1-svgrepo-com.svg",
        tags: ["food", "fruit", "lemon", "citrus"],
      },
      {
        name: "Burger Long",
        source: "/shapes/food/long-imitation-burger-1-svgrepo-com.svg",
        tags: ["food", "fast food", "burger"],
      },
      {
        name: "Triangle Sandwich",
        source: "/shapes/food/triangle-sandwich-1-svgrepo-com.svg",
        tags: ["food", "sandwich", "breakfast"],
      },
      {
        name: "Watermelon",
        source: "/shapes/food/watermelon-1-svgrepo-com.svg",
        tags: ["food", "fruit", "watermelon", "summer"],
      },
      {
        name: "Western Food",
        source: "/shapes/food/western-food-1-svgrepo-com.svg",
        tags: ["food", "western", "meal"],
      },
    ],
  },
  {
    name: "Ecommerce",
    description: "Shopping and ecommerce related shapes and icons",
    items: [
      {
        name: "Checkout List",
        source: "/shapes/ecommerce/checkout-list-checklist-svgrepo-com.svg",
        tags: ["ecommerce", "checkout", "list", "shopping"],
      },
      {
        name: "Closed Store",
        source: "/shapes/ecommerce/closed-store-info-svgrepo-com.svg",
        tags: ["ecommerce", "store", "closed", "shop"],
      },
      {
        name: "Customer Care",
        source: "/shapes/ecommerce/customer-care-hospital-svgrepo-com.svg",
        tags: ["ecommerce", "customer", "care", "service", "support"],
      },
      {
        name: "Delivery Box",
        source: "/shapes/ecommerce/delivery-box-shipping-svgrepo-com.svg",
        tags: ["ecommerce", "delivery", "shipping", "box", "logistics"],
      },
      {
        name: "Discount Sign",
        source: "/shapes/ecommerce/discount-sign-money-svgrepo-com.svg",
        tags: ["ecommerce", "discount", "sale", "money", "offer"],
      },
      {
        name: "Marketing Online",
        source: "/shapes/ecommerce/marketing-online-business-svgrepo-com.svg",
        tags: ["ecommerce", "marketing", "online", "business", "digital"],
      },
      {
        name: "Online Shop Phone",
        source: "/shapes/ecommerce/online-shop-phone-svgrepo-com.svg",
        tags: ["ecommerce", "online", "shop", "mobile", "phone"],
      },
      {
        name: "Online Shop Web",
        source: "/shapes/ecommerce/online-shop-web-svgrepo-com.svg",
        tags: ["ecommerce", "online", "shop", "web", "website"],
      },
      {
        name: "Open Store",
        source: "/shapes/ecommerce/open-store-info-svgrepo-com.svg",
        tags: ["ecommerce", "store", "open", "shop", "business"],
      },
      {
        name: "Payment Cards",
        source: "/shapes/ecommerce/payment-cards-money-svgrepo-com.svg",
        tags: ["ecommerce", "payment", "card", "credit", "money"],
      },
      {
        name: "Shopping Bag",
        source: "/shapes/ecommerce/shopping-bag-cart-svgrepo-com.svg",
        tags: ["ecommerce", "shopping", "bag", "cart", "purchase"],
      },
      {
        name: "Shopping Basket",
        source: "/shapes/ecommerce/shopping-basket-ecommerce-svgrepo-com.svg",
        tags: ["ecommerce", "shopping", "basket", "cart"],
      },
      {
        name: "Shopping Cart",
        source: "/shapes/ecommerce/shopping-cart-ecommerce-svgrepo-com.svg",
        tags: ["ecommerce", "shopping", "cart", "checkout"],
      },
      {
        name: "Store Shopping",
        source: "/shapes/ecommerce/store-shopping-ecommerce-svgrepo-com.svg",
        tags: ["ecommerce", "store", "shop", "retail"],
      },
      {
        name: "Verified Store",
        source: "/shapes/ecommerce/store-verified-shopping-svgrepo-com.svg",
        tags: ["ecommerce", "store", "verified", "trusted", "shop"],
      },
      {
        name: "Price Tag",
        source: "/shapes/ecommerce/tag-price-label-svgrepo-com.svg",
        tags: ["ecommerce", "tag", "price", "label", "cost"],
      },
      {
        name: "Sale Tag",
        source: "/shapes/ecommerce/tag-sale-shopping-svgrepo-com.svg",
        tags: ["ecommerce", "tag", "sale", "discount", "offer"],
      },
      {
        name: "Testimonial",
        source: "/shapes/ecommerce/testimoni-hand-finger-svgrepo-com.svg",
        tags: ["ecommerce", "testimonial", "review", "feedback"],
      },
      {
        name: "Voucher",
        source: "/shapes/ecommerce/voucher-discount-tag-svgrepo-com.svg",
        tags: ["ecommerce", "voucher", "discount", "coupon", "offer"],
      },
      {
        name: "Wallet",
        source: "/shapes/ecommerce/wallet-money-business-svgrepo-com.svg",
        tags: ["ecommerce", "wallet", "money", "payment", "finance"],
      },
    ],
  },
  {
    name: "Travel",
    description: "Fundamental geometric shapes for travel",
    items: [
      {
        name: "Airplane",
        source: "/shapes/travel/airplane-svgrepo-com.svg",
        tags: ["travel"],
      },
      {
        name: "Bench",
        source: "/shapes/travel/bench-svgrepo-com.svg",
        tags: ["geometric", "basic", "circle", "round"],
      },
      {
        name: "Cocktail",
        source: "/shapes/travel/cocktail-svgrepo-com.svg",
        tags: ["geometric", "basic", "triangle"],
      },
      {
        name: "Coconut Tree",
        source: "/shapes/travel/coconut-tree-svgrepo-com.svg",
        tags: ["geometric", "star", "decoration"],
      },
      {
        name: "Emu",
        source: "/shapes/travel/emu-svgrepo-com.svg",
        tags: ["geometric", "hexagon", "polygon"],
      },
      {
        name: "Hotel",
        source: "/shapes/travel/hotel-svgrepo-com.svg",
        tags: ["geometric", "hexagon", "polygon"],
      },
      {
        name: "Ice Cream",
        source: "/shapes/travel/ice-cream-svgrepo-com.svg",
        tags: ["geometric", "hexagon", "polygon"],
      },
      {
        name: "Indicator",
        source: "/shapes/travel/indicator-svgrepo-com.svg",
        tags: ["geometric", "hexagon", "polygon"],
      },
      {
        name: "Lighthouse",
        source: "/shapes/travel/lighthouse-svgrepo-com.svg",
        tags: ["geometric", "hexagon", "polygon"],
      },
      {
        name: "Map",
        source: "/shapes/travel/map-svgrepo-com.svg",
        tags: ["geometric", "hexagon", "polygon"],
      },
      {
        name: "Milk Tea",
        source: "/shapes/travel/milk-tea-svgrepo-com.svg",
        tags: ["geometric", "hexagon", "polygon"],
      },
      {
        name: "Polaroid",
        source: "/shapes/travel/polaroid-svgrepo-com.svg",
        tags: ["geometric", "hexagon", "polygon"],
      },
      {
        name: "Snow Mountain",
        source: "/shapes/travel/snow-mountain-svgrepo-com.svg",
        tags: ["geometric", "hexagon", "polygon"],
      },
      {
        name: "Suitcase",
        source: "/shapes/travel/suitcase-svgrepo-com.svg",
        tags: ["geometric", "hexagon", "polygon"],
      },
      {
        name: "Sunbathing",
        source: "/shapes/travel/sunbathing-svgrepo-com.svg",
        tags: ["geometric", "hexagon", "polygon"],
      },
      {
        name: "Surf",
        source: "/shapes/travel/surf-svgrepo-com.svg",
        tags: ["geometric", "hexagon", "polygon"],
      },
      {
        name: "Take a Bath",
        source: "/shapes/travel/take-a-bath-svgrepo-com.svg",
        tags: ["geometric", "hexagon", "polygon"],
      },
      {
        name: "Villa",
        source: "/shapes/travel/villa-svgrepo-com.svg",
        tags: ["geometric", "hexagon", "polygon"],
      },
      {
        name: "Wallet",
        source: "/shapes/travel/wallet-svgrepo-com.svg",
        tags: ["geometric", "hexagon", "polygon"],
      },
      {
        name: "Wine",
        source: "/shapes/travel/wine-svgrepo-com.svg",
        tags: ["geometric", "hexagon", "polygon"],
      },
    ],
  },
];

// Filter shapes based on search query and category
const filterShapesBySearchAndCategory = (library: ShapeCategory[], query: string, category: string): ShapeItem[] => {
  // If no query and category is "all", return all shapes from all categories
  if (!query && category === "all") {
    return library.flatMap((cat) => cat.items);
  }

  // Filter by category first
  const categoryFiltered = category === "all" ? library : library.filter((cat) => cat.name === category);

  // Get all items from the filtered categories
  const items = categoryFiltered.flatMap((cat) => cat.items);

  // If no search query, return all items from the filtered categories
  if (!query) {
    return items;
  }

  // Filter by search query (case insensitive)
  const lowerQuery = query.toLowerCase();
  return items.filter(
    (item) =>
      item.name.toLowerCase().includes(lowerQuery) || item.tags.some((tag) => tag.toLowerCase().includes(lowerQuery))
  );
};

export const ShapeInspector = () => {
  const { t } = useTranslation();
  const dispatchAddImage = useAddImageObject();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  // Filter shapes based on search and category
  const filteredShapes = useMemo(() => {
    return filterShapesBySearchAndCategory(shapeLibrary, searchQuery, activeCategory);
  }, [searchQuery, activeCategory]);

  const handleShapeSelect = (shape: ShapeItem) => {
    dispatchAddImage({ imageUrl: shape.source });
  };

  return (
    <div>
      <Block title={t("template.inspector.shapes")}>
        {/* Search bar */}
        <div className="mb-4">
          <input
            type="text"
            placeholder={t("common.search")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full p-2 border rounded"
          />
        </div>

        {/* Category tabs */}
        <Tabs defaultValue="all" onValueChange={setActiveCategory}>
          <TabsList className="mb-4">
            <TabsTrigger value="all">{t("common.all")}</TabsTrigger>
            {shapeLibrary.map((category) => (
              <TabsTrigger key={category.name} value={category.name}>
                {category.name}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Shape grid */}
          <ScrollArea className="h-full">
            <div className="grid grid-cols-3 gap-2">
              {filteredShapes.map((shape) => (
                <div
                  key={`${shape.name}-${shape.source}`}
                  className="p-2 border rounded hover:bg-gray-100 cursor-pointer flex flex-col items-center"
                  onClick={() => handleShapeSelect(shape)}
                >
                  <img src={shape.source} alt={shape.name} className="w-16 h-16 object-contain" />
                  <span className="text-xs mt-1">{shape.name}</span>
                </div>
              ))}
            </div>
          </ScrollArea>
        </Tabs>
      </Block>
    </div>
  );
};
